import { useMutation, useQuery } from "@tanstack/react-query";
import { xano, type BillingPlansResponse } from "@/lib/xano";
import { useCompany } from "@/lib/company";
import { SectionTitle } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { gbp } from "@/lib/format";
import { useState } from "react";

export function BillingSection() {
  const { activeToken } = useCompany();
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["billing-plans", activeToken],
    queryFn: () => xano.billing.plans(activeToken!),
    enabled: !!activeToken,
  });

  const subscribe = useMutation({
    mutationFn: (planKey: string) =>
      xano.billing.subscribe(activeToken!, planKey, window.location.origin + "/billing/return"),
    onSuccess: (res) => {
      if (res.ok && res.confirmation_url) {
        window.location.href = res.confirmation_url;
      } else {
        // Surface Shopify's reason (e.g. deprecated token / no shop connected).
        const sr = res.shopify_response as { errors?: unknown } | undefined;
        setError(
          typeof sr?.errors === "string"
            ? sr.errors
            : `Could not start checkout (status ${res.http_status}). Connect a live Shopify store first.`
        );
      }
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Subscribe failed."),
  });

  const d = q.data as BillingPlansResponse | undefined;
  if (!d) return null;
  const currentKey = d.current?.plan_key;

  return (
    <>
      <SectionTitle>Plan & billing</SectionTitle>
      {d.current ? (
        <div className="-mt-2 mb-3 text-sm text-muted-foreground">
          Current plan: <Badge variant="success">{d.current.plan_name}</Badge>{" "}
          {d.current.test && <Badge variant="secondary">test</Badge>}
        </div>
      ) : (
        <div className="-mt-2 mb-3 text-sm text-muted-foreground">
          No active subscription. Billing runs through Shopify.
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {d.plans.map((p) => {
          const isCurrent = p.plan_key === currentKey;
          return (
            <Card key={p.plan_key} className={isCurrent ? "border-primary/40" : ""}>
              <CardContent className="flex h-full flex-col p-5">
                <div className="font-medium">{p.name}</div>
                <div className="mt-1 font-display text-2xl font-semibold">
                  {p.price === 0 ? "Free" : gbp(p.price)}
                  {p.price > 0 && <span className="text-sm font-normal text-muted-foreground">/mo</span>}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{p.blurb}</p>
                <ul className="mt-3 flex-1 space-y-1.5">
                  {(p.features ?? []).map((f, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-xs">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-4">
                  {isCurrent ? (
                    <Button variant="secondary" className="w-full" disabled>
                      Current plan
                    </Button>
                  ) : p.price === 0 ? (
                    <Button variant="secondary" className="w-full" disabled>
                      Included
                    </Button>
                  ) : (
                    <Button
                      className="w-full"
                      disabled={subscribe.isPending}
                      onClick={() => {
                        setError(null);
                        subscribe.mutate(p.plan_key);
                      }}
                    >
                      {subscribe.isPending ? "Starting…" : "Choose " + p.name}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
    </>
  );
}
